import {
  Component,
  ChangeDetectionStrategy,
  OnChanges,
  SimpleChanges,
  inject,
  output,
  OnInit,
  input,
  viewChild,
  computed,
} from '@angular/core';
import { translation } from '@cue/translations';

import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { DatepickerComponent, DatetimepickerComponent, TimepickerComponent } from '@assist/ui/dateinputs';
import { PipesService } from '@cue/pipes';
import { distinctUntilChanged } from 'rxjs/operators';
import { startOfDay } from '@fullcalendar/core/internal';

@Component({
  selector: 'cue-date-time-separate-info',
  templateUrl: './date-time-separate-info.component.html',
  styleUrls: ['./date-time-separate-info.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TimepickerComponent, DatepickerComponent, ReactiveFormsModule],
})
export class DateTimeSeparateInfoComponent implements OnChanges, OnInit {
  protected pipesService = inject(PipesService);
  translation = translation;
  min = input<Date>();

  correctedMinTimePicker = computed(() => {
    if (this.min() && !this.dateOnly()) {
      const minDay = startOfDay(this.min()!);
      const dateTimeDay = startOfDay(new Date(this.dateTime()!));
      // if minDay is before dateTimeDay we will ignore min() for timepicker, otherwise we will use min()for timepicker
      if (minDay.getTime() < dateTimeDay.getTime()) {
        return null;
      }
    }
    return this.min();
  });
  edited = output<{
    oldValue: Date;
    newValue: Date;
  }>();
  dateOnly = input(false);
  max = input<Date>();
  timePickerAppendTo = input<'component' | 'root'>('component');
  public steps: any = { hour: 1, minute: 15, second: 0 };
  dateTime = input<string | Date>();
  editable = input(false);
  dateTimePicker = viewChild(DatetimepickerComponent);
  isStart = input<boolean>();
  type = input<'from' | 'to'>('from');

  dateControl: FormControl<Date> = new FormControl(new Date(), { nonNullable: true });
  timeControl: FormControl<Date> = new FormControl(new Date(), { nonNullable: true });

  ngOnInit() {
    this.dateControl.valueChanges.pipe(distinctUntilChanged()).subscribe((x) => {
      this.dateChange(x);
    });
    this.timeControl.valueChanges.pipe(distinctUntilChanged()).subscribe(() => {
      this.timeChange();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dateTime']) {
      const dateTime = this.toValidDate(this.dateTime());

      if (!dateTime) {
        return;
      }

      this.setControlValue(this.timeControl, dateTime);
      this.setControlValue(this.dateControl, dateTime);
    }
  }

  resetValue(value: Date): Date {
    const timeToUpdate = new Date(this.timeControl.value);
    timeToUpdate.setFullYear(value.getFullYear(), value.getMonth(), value.getDate());

    this.setControlValue(this.dateControl, timeToUpdate);
    this.setControlValue(this.timeControl, timeToUpdate);

    return timeToUpdate;
  }
  timeChange() {
    const updatedTime = this.timeControl.value;
    const dateToUpdate = new Date(this.dateControl.value);

    const oldValue = this.toValidDate(this.dateTime()) ?? new Date(this.dateControl.value);
    const currentDate = new Date(oldValue);
    dateToUpdate.setFullYear(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());

    dateToUpdate.setHours(updatedTime.getHours());
    dateToUpdate.setMinutes(updatedTime.getMinutes());
    dateToUpdate.setSeconds(updatedTime.getSeconds());

    this.setControlValue(this.dateControl, dateToUpdate);

    if (!this.sameDateTime(oldValue, dateToUpdate)) {
      this.edited.emit({ oldValue, newValue: dateToUpdate });
    }
  }

  dateChange(date: Date) {
    const timeToUpdate = new Date(this.timeControl.value);
    const oldValue = this.toValidDate(this.dateTime()) ?? new Date(timeToUpdate);
    timeToUpdate.setFullYear(date.getFullYear(), date.getMonth(), date.getDate());
    this.setControlValue(this.timeControl, timeToUpdate);

    if (!this.sameDateTime(oldValue, timeToUpdate)) {
      this.edited.emit({ oldValue, newValue: timeToUpdate });
    }
  }

  private setControlValue(control: FormControl<Date>, value: Date) {
    if (this.sameDateTime(control.value, value)) {
      return;
    }

    control.setValue(new Date(value), { emitEvent: false });
  }

  private toValidDate(value: string | Date | undefined): Date | null {
    if (!value) {
      return null;
    }

    const date = new Date(value);

    return Number.isNaN(date.getTime()) ? null : date;
  }

  private sameDateTime(left: Date, right: Date): boolean {
    return left.getTime() === right.getTime();
  }
}
